import {
    ChangeDetectionStrategy, AfterViewInit,
    Component,
    computed,
    ElementRef,
    inject,
    model,
    NgZone,
    OnDestroy,
    viewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FtDaterangeCalendar } from './ft-daterange-calendar';

/**
 * Native port of the AngularJS `ftDateRangePicker` / `ftDateRange` / `ftShowDateRange`
 * directives. Renders the `.ft-dropdown.date-range` trigger with its `MMMM D, YYYY` display text and
 * opens {@link FtDaterangeCalendar} — the in-tree replacement for the jQuery `daterangepicker`
 * plugin, which emits the same DOM and so inherits the same vendored CSS.
 *
 * The two-way `startDate`/`endDate` models are format-agnostic: they hold whatever the caller bound
 * (moment at contest-details, dayjs at create-contest-modal) until the first pick, after which they
 * hold dayjs. `displayText` only ever calls `.format()`, which all three share.
 */
@Component({
    selector: 'ft-date-range-picker-ng',
    imports: [],
    encapsulation: ViewEncapsulation.None,
    templateUrl: './ft-date-range-picker.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FtDateRangePickerComponent implements AfterViewInit, OnDestroy {
    readonly startDate = model<any>();
    readonly endDate = model<any>();

    private readonly toggle = viewChild.required<ElementRef<HTMLElement>>('toggle');

    private readonly zone = inject(NgZone);
    private picker: FtDaterangeCalendar | null = null;
    private readonly keyHandler = (event: KeyboardEvent) => {
        if (event.which === 27 || event.keyCode === 27) this.picker?.hide();
    };

    /** v1 ftShowDateRange (default format 'MMMM D, YYYY'). */
    protected readonly displayText = computed<string>(() => {
        const startDate = this.startDate();
        const endDate = this.endDate();
        if (!startDate || !endDate) return '';
        return startDate.format('MMMM D, YYYY') + ' - ' + endDate.format('MMMM D, YYYY');
    });

    ngAfterViewInit(): void {
        // v1 initialize(). The old show()/hide() "positioning dance" that followed is GONE: it only
        // existed to force the jQuery plugin to compute an initial offset, and FtDaterangeCalendar
        // positions itself on every show().
        this.picker = new FtDaterangeCalendar(
            this.toggle().nativeElement,
            { startDate: this.startDate(), endDate: this.endDate() },
            (start, end) => {
                // The calendar's callback runs from a DOM listener, i.e. outside Angular's zone →
                // run in-zone so the display + the two-way outputs propagate (v1 did this in
                // scope.$apply).
                this.zone.run(() => {
                    this.startDate.set(start);
                    this.endDate.set(end);
                });
            },
        );
        document.addEventListener('keydown', this.keyHandler);
        document.addEventListener('keypress', this.keyHandler);
    }

    ngOnDestroy(): void {
        document.removeEventListener('keydown', this.keyHandler);
        document.removeEventListener('keypress', this.keyHandler);
        this.picker?.remove();
        this.picker = null;
    }
}
